/**
 * /settings/displacements (v9) — cadastro de Deslocamentos no padrão TiFlux:
 * breadcrumb Configurações / Financeiro / Deslocamentos, busca client-side,
 * "+ Deslocamento" teal à direita e tabela Nome | Valor | Referente a |
 * Cliente | Contrato | ações. Dialog: Nome* + Valor* (R$) + Cliente (default
 * Todos) + Contrato opcional do cliente escolhido. Leitura livre p/ staff;
 * escrita exige contracts.manage (botões escondidos sem a permissão).
 */
import { useState, type FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { usePermissions } from '@/auth/use-permissions';
import { Button } from '@/components/ui/button';
import { Input, Label, Select } from '@/components/ui/input';
import { Dialog, EmptyState, Skeleton } from '@/components/ui/misc';
import { DeskRowAction } from '@/features/desks/desk-stages-section';
import { clientsLookupOptions } from '@/features/desks/desks-queries';
import {
  matchesText,
  normalizeText,
  SettingsBreadcrumb,
  SettingsSearch,
} from '@/features/settings/settings-ui';
import type { Displacement } from '@erp/shared';
import { contractsOptions, displacementsOptions, errorMessage } from './billing-queries';
import { useDeleteDisplacement, useSaveDisplacement } from './billing-mutations';
import { formatBRL, formatBRLInput, parseBRLInput } from './billing-format';
import { MoneyInput } from './contract-drawer';

/** 'Todos' sem cliente; 'Contrato' quando escopado a um contrato; senão 'Cliente'. */
function referenteA(d: Displacement): string {
  if (d.clientId === null) return 'Todos';
  return d.contractId !== null ? 'Contrato' : 'Cliente';
}

export function DisplacementsSettingsPage() {
  const { can } = usePermissions();
  const canManage = can('contracts.manage');
  const listQ = useQuery(displacementsOptions());
  const remove = useDeleteDisplacement();
  const [query, setQuery] = useState('');
  const [dialog, setDialog] = useState<{ displacement: Displacement | null } | null>(null);

  const q = normalizeText(query.trim());
  const list = (listQ.data ?? []).filter(
    (d) =>
      q === '' ||
      matchesText(d.name, q) ||
      matchesText(d.clientName ?? '', q) ||
      matchesText(d.contractName ?? '', q)
  );

  return (
    <div className="rounded-lg border bg-card p-4 shadow-xs sm:p-5">
      <SettingsBreadcrumb section="Financeiro" page="Deslocamentos" />
      <div className="mt-2 flex flex-wrap items-center gap-3">
        <h1 className="text-xl leading-7 font-semibold">Deslocamentos</h1>
        <div className="ml-auto flex items-center gap-2">
          <SettingsSearch value={query} onChange={setQuery} label="Buscar deslocamento" />
          {canManage ? (
            <Button size="sm" className="h-9" onClick={() => setDialog({ displacement: null })}>
              <Plus className="size-4" strokeWidth={2.5} />
              Deslocamento
            </Button>
          ) : null}
        </div>
      </div>

      <div className="mt-4">
        {listQ.isPending ? (
          <div className="space-y-2" aria-busy>
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : listQ.isError ? (
          <EmptyState message={`Erro ao carregar deslocamentos: ${errorMessage(listQ.error)}`} />
        ) : list.length === 0 ? (
          <EmptyState
            className="h-52"
            message={q !== '' ? 'Nenhum deslocamento encontrado' : 'Não há dados'}
          />
        ) : (
          <div className="overflow-x-auto rounded-md border">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted text-left text-[13px]">
                  <th className="h-10 px-4 font-semibold">Nome</th>
                  <th className="h-10 w-36 px-4 font-semibold">Valor</th>
                  <th className="h-10 w-40 px-4 font-semibold">Referente a</th>
                  <th className="h-10 px-4 font-semibold">Cliente</th>
                  <th className="h-10 px-4 font-semibold">Contrato</th>
                  <th className="h-10 w-24 px-4">
                    <span className="sr-only">Ações</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {list.map((d) => (
                  <tr
                    key={d.id}
                    className="h-11 border-b border-divider transition-colors last:border-0 hover:bg-accent/60"
                  >
                    <td className="max-w-72 truncate px-4 font-medium">{d.name}</td>
                    <td className="px-4 tabular-nums whitespace-nowrap">{formatBRL(d.value)}</td>
                    <td className="px-4">{referenteA(d)}</td>
                    <td className="max-w-56 truncate px-4">{d.clientName ?? '-'}</td>
                    <td className="max-w-56 truncate px-4">{d.contractName ?? '-'}</td>
                    <td className="px-4">
                      {canManage ? (
                        <div className="flex items-center justify-end gap-0.5">
                          <DeskRowAction
                            label="Editar deslocamento"
                            onClick={() => setDialog({ displacement: d })}
                          >
                            <Pencil className="size-3.5" />
                          </DeskRowAction>
                          <DeskRowAction
                            label="Excluir deslocamento"
                            destructive
                            disabled={remove.isPending}
                            onClick={() => {
                              if (window.confirm(`Excluir o deslocamento "${d.name}"?`)) {
                                remove.mutate(d.id);
                              }
                            }}
                          >
                            <Trash2 className="size-3.5" />
                          </DeskRowAction>
                        </div>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {remove.error ? (
        <p className="mt-2 text-sm text-destructive">{errorMessage(remove.error)}</p>
      ) : null}

      {dialog ? (
        <DisplacementDialog displacement={dialog.displacement} onClose={() => setDialog(null)} />
      ) : null}
    </div>
  );
}

function DisplacementDialog({
  displacement,
  onClose,
}: {
  displacement: Displacement | null;
  onClose: () => void;
}) {
  const save = useSaveDisplacement();
  const clientsQ = useQuery(clientsLookupOptions());

  const [name, setName] = useState(displacement?.name ?? '');
  const [valueText, setValueText] = useState(
    displacement ? formatBRLInput(displacement.value) : ''
  );
  const [clientId, setClientId] = useState<number | ''>(displacement?.clientId ?? '');
  const [contractId, setContractId] = useState<number | ''>(displacement?.contractId ?? '');

  // Contratos só do cliente escolhido (o dialog do TiFlux só mostra o select
  // de contrato depois de escolher um cliente).
  const contractsQ = useQuery({
    ...contractsOptions({ client_ids: clientId === '' ? [] : [clientId], status: 'all' }),
    enabled: clientId !== '',
  });

  const value = parseBRLInput(valueText);
  const valid = name.trim() !== '' && value !== null;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!valid || value === null) return;
    save.mutate(
      {
        id: displacement?.id ?? null,
        body: {
          name: name.trim(),
          value,
          clientId: clientId === '' ? null : clientId,
          contractId: clientId === '' || contractId === '' ? null : contractId,
        },
      },
      { onSuccess: onClose }
    );
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={displacement ? 'Editar deslocamento' : 'Novo deslocamento'}
    >
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="displacement-name">
            Nome <span className="text-destructive">*</span>
          </Label>
          <Input
            id="displacement-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            autoFocus
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="displacement-value">
            Valor <span className="text-destructive">*</span>
          </Label>
          <MoneyInput id="displacement-value" value={valueText} onChange={setValueText} required />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="displacement-client">Cliente</Label>
          <Select
            id="displacement-client"
            value={clientId === '' ? '' : String(clientId)}
            onChange={(e) => {
              const next = e.target.value === '' ? '' : Number(e.target.value);
              setClientId(next);
              setContractId('');
            }}
          >
            <option value="">Todos</option>
            {(clientsQ.data ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>
        </div>
        {clientId !== '' ? (
          <div className="space-y-1.5">
            <Label htmlFor="displacement-contract">Contrato</Label>
            <Select
              id="displacement-contract"
              value={contractId === '' ? '' : String(contractId)}
              onChange={(e) =>
                setContractId(e.target.value === '' ? '' : Number(e.target.value))
              }
              disabled={contractsQ.isPending}
            >
              <option value="">Todos os contratos do cliente</option>
              {(contractsQ.data ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          </div>
        ) : null}
        {save.error ? <p className="text-sm text-destructive">{errorMessage(save.error)}</p> : null}
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" size="sm" disabled={save.isPending || !valid}>
            {save.isPending ? 'Salvando…' : 'Salvar'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
